import { Col, Form, Row } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { DetailList } from 'components/details/DetailList';
import { EditString } from 'components/forms/EditString';
import { EditText } from 'components/forms/EditText';
import { EditDialog } from 'edit/EditDialog';
import type { EditModel } from 'edit/EditModel';
import { EditFolderReference } from 'edit/reference/EditFolderReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';
import { EditZoneReference } from 'edit/reference/EditZoneReference';
import type { SchedulingWindow } from 'api/scheduling_windows/endpoints/getSchedulingWindow';

import type { EditableSchedulingWindow } from './EditableSchedulingWindow';
import { ScheduleRuleCard } from './ScheduleRuleCard';
import { WindowLinkRow } from './WindowLinkRow';

interface Props {
  readonly model: EditModel<EditableSchedulingWindow, SchedulingWindow | null>;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

export const EditSchedulingWindowDialog = ({ model, onSaved, onClose }: Props) => (
  <EditDialog model={model} onSaved={onSaved} onClose={onClose} entityName="Scheduling Window">
    {(entity) => <FormFields entity={entity} />}
  </EditDialog>
);

interface FieldsProps {
  readonly entity: EditableSchedulingWindow;
}

const FormFields = observer<FieldsProps>(({ entity }) => (
  <>
    <Row>
      <Col md={12}>
        <EditString label="Name" value={entity.name} onChange={(v) => (entity.name = v)} mandatory />
      </Col>
    </Row>

    <Row>
      <Col md={6}>
        <EditZoneReference
          value={entity.zone}
          onChange={(zone) => {
            // Folders are zone-scoped — a zone change invalidates the folder.
            entity.zone = zone;
            entity.folder = null;
          }}
          resourceType="scheduling_windows"
          action={entity.isCreating ? 'create' : 'update'}
          mandatory
        />
      </Col>
      <Col md={6}>
        <EditFolderReference value={entity.folder} onChange={(v) => (entity.folder = v)} zoneName={entity.zone?.name} />
      </Col>
    </Row>

    <Row>
      <Col md={12}>
        <EditText label="Description" value={entity.description} onChange={(v) => (entity.description = v)} />
      </Col>
    </Row>

    <Row>
      <Col md={12}>
        <EditTimeZoneReference label="Timezone" value={entity.timezone} onChange={(v) => (entity.timezone = v)} mandatory />
        <Form.Text className="text-muted">The zone this window&apos;s rules are read in, unless a rule overrides it.</Form.Text>
      </Col>
    </Row>

    <DetailList
      items={entity.schedules}
      getKey={(schedule) => schedule.key}
      onAdd={entity.createSchedule}
      addLabel="Add rule"
      onReorder={entity.moveSchedule}
      renderItem={(schedule, index, dragHandle) => <ScheduleRuleCard schedule={schedule} index={index + 1} dragHandle={dragHandle} />}
    />

    <DetailList
      items={entity.links}
      getKey={(link) => link.key}
      onAdd={entity.createLink}
      addLabel="Add linked window"
      renderItem={(link) => <WindowLinkRow link={link} />}
    />
  </>
));
